import React, { useState } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle, AlertCircle, Landmark } from 'lucide-react';
import { SubscriptionInfo } from './types';

interface PaymentUpdateFormProps {
  subscriptionId: string;
  clientSecret: string;
  subscriptionInfo: SubscriptionInfo;
  stripeAccountId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const PaymentUpdateForm: React.FC<PaymentUpdateFormProps> = ({
  subscriptionId,
  clientSecret,
  subscriptionInfo,
  stripeAccountId,
  onSuccess,
  onCancel
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isElementsReady, setIsElementsReady] = useState(false);

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatInterval = (interval: string) => {
    switch (interval) {
      case 'month': return 'monthly';
      case 'year': return 'yearly';
      case 'week': return 'weekly';
      case 'day': return 'daily';
      default: return interval;
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
  event.preventDefault();

  if (!stripe || !elements) {
    setError('Please wait for the form to load.');
    return;
  }

  setIsLoading(true);
  setError(null);

  const paymentElement = elements.getElement(PaymentElement);
  if (!paymentElement) {
    setError('Payment form is not ready yet. Please wait a moment and try again.');
    setIsLoading(false);
    return;
  }

  try {
    console.log('🚀 === PAYMENT METHOD UPDATE START ===');
    console.log('🔧 Configuration:');
    console.log('  Stripe account ID:', stripeAccountId);
    console.log('  Subscription ID:', subscriptionId);
    console.log('  Client Secret present:', !!clientSecret);
    console.log('  Environment:', window.location.origin);
    
    console.log('⚡ Calling stripe.confirmSetup...');
    const result = await stripe.confirmSetup({
      elements,
      redirect: 'if_required', // Evita redirección automática
      // Eliminamos confirmParams para evitar redirección
    });

    console.log('📊 Stripe confirmSetup result received:');
    console.log('  Result type:', typeof result);
    console.log('  Has error:', !!result.error);
    console.log('  Has setupIntent:', 'setupIntent' in result);
    
    // Detailed logging of the result
    if (result.error) {
      console.error('❌ Setup confirmation error:', result.error);
      console.error('Error type:', result.error.type);
      console.error('Error code:', result.error.code);
      console.error('Error message:', result.error.message);
      console.error('Error decline code:', result.error.decline_code);
      console.error('Error payment method:', result.error.payment_method);
      
      // Check for specific error types
      if (result.error.type === 'validation_error') {
        setError(`Validation Error: ${result.error.message}`);
      } else if (result.error.type === 'card_error') {
        setError(`Payment Error: ${result.error.message}`);
      } else if (result.error.type === 'api_connection_error') {
        setError('Connection Error: Unable to connect to payment processor. Please try again.');
      } else if (result.error.type === 'api_error') {
        setError('API Error: There was an issue processing your request. Please try again.');
      } else if (result.error.type === 'authentication_error') {
        setError('Authentication Error: There was an issue with your request. Please refresh and try again.');
      } else if (result.error.type === 'rate_limit_error') {
        setError('Rate Limit Error: Too many requests. Please wait a moment and try again.');
      } else {
        setError(`Setup failed: ${result.error.message}`);
      }
      
    } else if ('setupIntent' in result && result.setupIntent) {
      const setupIntent = result.setupIntent as any;
      console.log('✅ Setup Intent details:');
      console.log('  ID:', setupIntent.id);
      console.log('  Status:', setupIntent.status);
      console.log('  Payment Method:', setupIntent.payment_method);
      console.log('  Customer:', setupIntent.customer);
      console.log('  Metadata:', setupIntent.metadata);
      
      if (setupIntent.status === 'succeeded') {
        console.log('🎉 Setup completed successfully!');
        console.log('🔄 Calling webhook manually to update Supabase...');
        
        // call the webhook manually to update Supabase
        try {
          const webhookResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-webhook-payment-update`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              type: 'setup_intent.succeeded',
              data: {
                object: {
                  id: setupIntent.id,
                  customer: setupIntent.customer || 'will_be_retrieved_from_subscription',
                  payment_method: setupIntent.payment_method,
                  metadata: {
                    subscription_id: subscriptionId,
                    stripe_account_id: stripeAccountId,
                    purpose: 'subscription_payment_method_update'
                  }
                }
              },
              account: stripeAccountId
            })
          });

          if (webhookResponse.ok) {
            console.log('✅ Webhook called successfully, Supabase should be updated');
            setError('✅ Payment method updated in both Stripe and Supabase!');
          } else {
            console.error('❌ Webhook call failed:', await webhookResponse.text());
            setError('✅ Payment method updated in Stripe, but Supabase update failed. Please check logs.');
          }
        } catch (webhookError) {
          console.error('❌ Error calling webhook:', webhookError);
          setError('✅ Payment method updated in Stripe, but failed to update Supabase. Please check logs.');
        }
        
        // wait 2 seconds and show result
        setTimeout(() => {
          console.log('⏰ Webhook processing complete.');
          setIsComplete(true);
        }, 2000);
        
      } else if (setupIntent.status === 'requires_action') {
        console.log('⚠️ Setup requires additional action');
        setError('Additional verification required. Please follow the instructions.');
      } else if (setupIntent.status === 'processing') {
        console.log('⏳ Setup is processing...');
        setError('Your payment method is being verified. This may take a few moments.');
      } else {
        console.log('⚠️ Unexpected setup intent status:', setupIntent.status);
        setError(`Setup completed with status: ${setupIntent.status}. Please contact support if you don't see the update.`);
      }
    } else {
      console.error('🤷 Unexpected result structure:', result);
      setError('Unexpected response from payment processor. Please try again.');
    }
    
    console.log('🏁 === PAYMENT METHOD UPDATE END ===');
    
  } catch (err) {
    console.error('💥 Error in handleSubmit:', err);
    console.error('Error type:', typeof err);
    console.error('Error constructor:', err?.constructor?.name);
    setError('An unexpected error occurred. Please try again.');
  } finally {
    setIsLoading(false);
  }
};


  if (isComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 pt-20">
        <div className="container mx-auto px-4 max-w-md">
          <Card className="w-full">
            <CardContent className="p-6 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-green-700 mb-2">
                Payment Method Updated Successfully
              </h3>
              <p className="text-gray-600 mb-4">
                ✅ Setup Intent completed successfully<br/>
                🔔 Webhook should have executed in Supabase<br/>
                📊 Check Supabase logs for webhook execution details
              </p>
              <div className="text-sm text-gray-500 space-y-1">
                <p><strong>Account:</strong> {stripeAccountId || 'Main account'}</p>
                <p><strong>Subscription:</strong> {subscriptionId}</p>
                <p>The subscription payment method should now be updated.</p>
              </div>
              <div className="mt-4">
                <Button 
                  onClick={onSuccess}
                  className="w-full"
                >
                  Continue
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 pt-20">
      <div className="container mx-auto px-4 max-w-2xl">
        <Card className="w-full">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-2">
              <Landmark className="w-8 h-8 text-blue-500" />
            </div>
            <CardTitle className="text-2xl text-gray-800">Update to Bank Account</CardTitle>
            <div className="text-sm text-gray-600 space-y-1">
              <p><strong>Subscription:</strong> {subscriptionInfo.product_name || 'Subscription'}</p>
              <p><strong>Amount:</strong> {formatCurrency(subscriptionInfo.amount, subscriptionInfo.currency)} {formatInterval(subscriptionInfo.interval)}</p>
              {subscriptionInfo.current_payment_method && (
                <p><strong>Current method:</strong> {subscriptionInfo.current_payment_method.type === 'card' 
                  ? `${subscriptionInfo.current_payment_method.brand?.toUpperCase()} •••• ${subscriptionInfo.current_payment_method.last4}`
                  : 'Bank account'
                }</p>
              )}
            </div>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {error}
                    {error.includes('Failed to fetch') && (
                      <div className="mt-2 text-sm">
                        <p><strong>This might be caused by:</strong></p>
                        <ul className="list-disc list-inside mt-1 space-y-1">
                          <li>Network connectivity issues</li>
                          <li>Ad blocker blocking Stripe (try disabling it)</li>
                          <li>Server temporarily unavailable</li>
                        </ul>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {/* Payment Method Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-700">New Payment Method</h3>
                
                {/* Bank Account Info Card */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Landmark className="h-6 w-6 text-blue-600" />
                    <div>
                      <div className="font-semibold text-base text-blue-900">Bank Account (ACH)</div>
                      <div className="text-sm text-blue-700">
                        Lower fees • Secure bank transfer • Takes 3-5 business days
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Element */}
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-600">
                  Enter Bank Account Details
                </h4>
                  {!isElementsReady && (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      <span className="text-sm text-gray-500">Loading payment form...</span>
                    </div>
                  )}
                  <PaymentElement 
                    options={{
                      layout: 'tabs',
                      business: {
                        name: 'KeepMore'
                      },
                      fields: {
                        billingDetails: 'auto'
                      },
                      wallets: {
                        applePay: 'never',
                        googlePay: 'never'
                      }
                    }}
                    onReady={() => {
                      setIsElementsReady(true);
                    }}
                    onLoadError={(error) => {
                      setError('Failed to load payment form. Please refresh the page and try again.');
                    }}
                  />
              </div>
              
              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isLoading}
                  className="flex-1 text-black hover:bg-red-500 hover:text-white"
                >
                  Cancel
                </Button>
                
                <Button
                  type="submit"
                  disabled={!stripe || !isElementsReady || isLoading}
                  className="flex-1"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update Payment Method'
                  )}
                </Button>
              </div>
            </form>
            
            <div className="mt-6 text-xs text-gray-500 text-center">
              <p>Your payment information is encrypted and secure.</p>
              <p>We use Stripe to process payments securely.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentUpdateForm;